import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useSyncExternalStore,
} from 'react';
import type {
  UnsavedChangesSectionHandlers,
  UnsavedChangesSnapshot,
  UnsavedChangesStore,
} from './unsavedChangesStore';

export const UnsavedChangesContext = createContext<UnsavedChangesStore | null>(
  null
);

const EMPTY_SNAPSHOT: UnsavedChangesSnapshot = {
  changeCount: 0,
  dirtySectionIds: [],
  isSaving: false,
  errors: {},
};

const noopSubscribe = () => () => {};

/** The page's store, or null outside an `UnsavedChangesProvider`. */
export const useUnsavedChangesStore = () => useContext(UnsavedChangesContext);

/** The page's unsaved-changes state, re-rendering when it changes. */
export const useUnsavedChangesSnapshot = (): UnsavedChangesSnapshot => {
  const store = useUnsavedChangesStore();
  return useSyncExternalStore(
    store?.subscribe ?? noopSubscribe,
    store?.getSnapshot ?? (() => EMPTY_SNAPSHOT)
  );
};

/**
 * Registers one editable section with the page's store and reports its
 * change count. Returns the error of the section's last failed save, if any.
 * Outside a provider it does nothing, so a section still renders on its own.
 */
export const useUnsavedChangesSection = (
  id: string,
  handlers: UnsavedChangesSectionHandlers,
  changes: number
): unknown => {
  const store = useUnsavedChangesStore();

  // Layout effects: registered before any sibling effect can save the page.
  useLayoutEffect(() => store?.register(id, handlers), [store, id]); // eslint-disable-line react-hooks/exhaustive-deps
  useLayoutEffect(() => store?.update(id, handlers), [store, id, handlers]);
  useEffect(() => store?.setChanges(id, changes), [store, id, changes]);

  const snapshot = useUnsavedChangesSnapshot();
  return snapshot.errors[id];
};
